import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useColorModeValue,
  useToast,
  useDisclosure,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  ArrowBackIcon,
  ViewIcon,
  DownloadIcon,
  CheckCircleIcon,
} from '@chakra-ui/icons';
import Card from 'components/card/Card';
import { getPayrollsByPeriod, updatePayrollStatus } from 'services/payroll.service';
import CalculatePayrollModal from 'components/payroll/CalculatePayrollModal';
import PayrollDetailModal from 'components/payroll/PayrollDetailModal';

export default function PayrollListView({ period, onBack }) {
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayroll, setSelectedPayroll] = useState(null);

  const {
    isOpen: isCalculateOpen,
    onOpen: onCalculateOpen,
    onClose: onCalculateClose,
  } = useDisclosure();

  const {
    isOpen: isDetailOpen,
    onOpen: onDetailOpen,
    onClose: onDetailClose,
  } = useDisclosure();

  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const bgCard = useColorModeValue('white', 'navy.800');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

  useEffect(() => {
    loadPayrolls();
  }, [period]);

  const loadPayrolls = async () => {
    try {
      setLoading(true);
      const response = await getPayrollsByPeriod(period.id);
      console.log('📦 Respuesta liquidaciones:', response);
      
      if (response.status === 200 || response.data?.status === 200) {
        // Manejar diferentes formatos de respuesta
        const payrollsData = 
          response.data?.payload?.data || 
          response.payload?.data || 
          response.data?.payload?.payrolls ||
          response.payload?.payrolls || 
          [];
        
        console.log('✅ Liquidaciones extraídas:', payrollsData);
        setPayrolls(payrollsData);
      }
    } catch (error) {
      console.error('❌ Error cargando liquidaciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las liquidaciones',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (payroll) => {
    setSelectedPayroll(payroll);
    onDetailOpen();
  };

  const handleApproveAll = async () => {
    try {
      setLoading(true);
      
      // Filtrar solo las liquidaciones que NO están aprobadas o pagadas
      const pendingPayrolls = payrolls.filter(
        p => p.status !== 'APPROVED' && p.status !== 'PAID'
      );

      if (pendingPayrolls.length === 0) {
        toast({
          title: 'Información',
          description: 'Todas las liquidaciones ya están aprobadas',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Aprobar todas las liquidaciones pendientes
      const promises = pendingPayrolls.map(payroll =>
        updatePayrollStatus(payroll.id, { status: 'APPROVED' })
      );

      await Promise.all(promises);

      toast({
        title: 'Éxito',
        description: `${pendingPayrolls.length} liquidación(es) aprobadas correctamente`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      loadPayrolls();
    } catch (error) {
      console.error('Error aprobando liquidaciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron aprobar todas las liquidaciones',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'gray';
      case 'CALCULATED':
        return 'yellow';
      case 'APPROVED':
        return 'green';
      case 'PAID':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'Borrador';
      case 'CALCULATED':
        return 'Calculado';
      case 'APPROVED':
        return 'Aprobado';
      case 'PAID':
        return 'Pagado';
      default:
        return status;
    }
  };

  const getMonthName = (month) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[month - 1];
  };

  // Calcular totales
  const totals = payrolls.reduce(
    (acc, p) => ({
      gross: acc.gross + (p.gross_salary || 0),
      irpf: acc.irpf + (p.irpf || 0),
      bps: acc.bps + (p.bps || 0),
      fonasa: acc.fonasa + (p.fonasa || 0),
      net: acc.net + (p.net_salary || 0),
    }),
    { gross: 0, irpf: 0, bps: 0, fonasa: 0, net: 0 }
  );

  if (loading) return null;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} gap="20px" mb="20px">
        <Card>
          <Stat>
            <StatLabel>Total Bruto</StatLabel>
            <StatNumber fontSize="2xl">
              ${' '}
              {totals.gross.toLocaleString('es-UY', {
                minimumFractionDigits: 2,
              })}
            </StatNumber>
            <StatHelpText>{payrolls.length} empleados</StatHelpText>
          </Stat>
        </Card>
        <Card>
          <Stat>
            <StatLabel>IRPF</StatLabel>
            <StatNumber fontSize="2xl" color="red.500">
              ${' '}
              {totals.irpf.toLocaleString('es-UY', {
                minimumFractionDigits: 2,
              })}
            </StatNumber>
          </Stat>
        </Card>
        <Card>
          <Stat>
            <StatLabel>BPS</StatLabel>
            <StatNumber fontSize="2xl" color="orange.500">
              ${' '}
              {totals.bps.toLocaleString('es-UY', {
                minimumFractionDigits: 2,
              })}
            </StatNumber>
          </Stat>
        </Card>
        <Card>
          <Stat>
            <StatLabel>FONASA</StatLabel>
            <StatNumber fontSize="2xl" color="blue.500">
              ${' '}
              {totals.fonasa.toLocaleString('es-UY', {
                minimumFractionDigits: 2,
              })}
            </StatNumber>
          </Stat>
        </Card>
        <Card>
          <Stat>
            <StatLabel>Total Líquido</StatLabel>
            <StatNumber fontSize="2xl" color="green.500">
              ${' '}
              {totals.net.toLocaleString('es-UY', {
                minimumFractionDigits: 2,
              })}
            </StatNumber>
          </Stat>
        </Card>
      </SimpleGrid>

      <Card bg={bgCard} p="20px">
        {/* Header */}
        <Flex justify="space-between" align="center" mb="20px">
          <Flex align="center" gap={3}>
            <IconButton
              icon={<ArrowBackIcon />}
              variant="ghost"
              onClick={onBack}
              aria-label="Volver"
            />
            <Box>
              <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                Liquidaciones - {getMonthName(period.month)} {period.year}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {period.description || 'Sin descripción'}
              </Text>
            </Box>
          </Flex>
          <Flex gap={2}>
            {payrolls.length > 0 && (
              <Button
                leftIcon={<CheckCircleIcon />}
                colorScheme="green"
                variant="outline"
                onClick={handleApproveAll}
              >
                Aprobar Todas
              </Button>
            )}
          </Flex>
        </Flex>

        {/* Table */}
        <Box overflowX="auto">
          <Table variant="simple" color={textColor} size="sm">
            <Thead>
              <Tr>
                <Th borderColor={borderColor}>Empleado</Th>
                <Th borderColor={borderColor}>Documento</Th>
                <Th borderColor={borderColor} isNumeric>
                  Sueldo Bruto
                </Th>
                <Th borderColor={borderColor} isNumeric>
                  IRPF
                </Th>
                <Th borderColor={borderColor} isNumeric>
                  BPS
                </Th>
                <Th borderColor={borderColor} isNumeric>
                  FONASA
                </Th>
                <Th borderColor={borderColor} isNumeric>
                  Líquido
                </Th>
                <Th borderColor={borderColor}>Estado</Th>
                <Th borderColor={borderColor}>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {payrolls.length === 0 ? (
                <Tr>
                  <Td colSpan={9} textAlign="center" py="40px">
                    <Text color="gray.500">
                      No hay liquidaciones calculadas para este período
                    </Text>
                  </Td>
                </Tr>
              ) : (
                payrolls.map((payroll) => {
                  // Adaptar la estructura de datos del backend
                  const employeeName = payroll.employee?.person?.name && payroll.employee?.person?.lastName
                    ? `${payroll.employee.person.name} ${payroll.employee.person.lastName}`
                    : payroll.employee_name || 'Sin nombre';
                  
                  const employeeDocument = payroll.employee?.person?.document || payroll.employee_document || '-';
                  
                  return (
                    <Tr key={payroll.id}>
                      <Td borderColor={borderColor}>
                        <Text fontWeight="500">{employeeName}</Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        <Text fontSize="sm">{employeeDocument}</Text>
                      </Td>
                      <Td borderColor={borderColor} isNumeric>
                        ${' '}
                        {(payroll.gross_salary || 0).toLocaleString('es-UY', {
                          minimumFractionDigits: 2,
                        })}
                      </Td>
                      <Td borderColor={borderColor} isNumeric>
                        ${' '}
                        {(payroll.irpf || 0).toLocaleString('es-UY', {
                          minimumFractionDigits: 2,
                        })}
                      </Td>
                      <Td borderColor={borderColor} isNumeric>
                        ${' '}
                        {(payroll.bps || 0).toLocaleString('es-UY', {
                          minimumFractionDigits: 2,
                        })}
                      </Td>
                      <Td borderColor={borderColor} isNumeric>
                        ${' '}
                        {(payroll.fonasa || 0).toLocaleString('es-UY', {
                          minimumFractionDigits: 2,
                        })}
                      </Td>
                      <Td borderColor={borderColor} isNumeric>
                        <Text fontWeight="600" color="green.500">
                          ${' '}
                          {(payroll.net_salary || 0).toLocaleString('es-UY', {
                            minimumFractionDigits: 2,
                          })}
                        </Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        <Badge
                          colorScheme={getStatusColor(payroll.status)}
                        >
                          {getStatusLabel(payroll.status)}
                        </Badge>
                      </Td>
                      <Td borderColor={borderColor}>
                        <IconButton
                          icon={<ViewIcon />}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => handleViewDetail(payroll)}
                          aria-label="Ver detalle"
                        />
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </Box>
      </Card>

      {/* Modal de calcular */}
      {isCalculateOpen && (
        <CalculatePayrollModal
          isOpen={isCalculateOpen}
          onClose={onCalculateClose}
          period={period}
          onSuccess={() => {
            loadPayrolls();
            onCalculateClose();
          }}
        />
      )}

      {/* Modal de detalle */}
      {isDetailOpen && selectedPayroll && (
        <PayrollDetailModal
          isOpen={isDetailOpen}
          onClose={onDetailClose}
          payroll={selectedPayroll}
          onSuccess={() => {
            loadPayrolls();
            onDetailClose();
          }}
        />
      )}
    </Box>
  );
}
